import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, GraduationCap, Heart, CalendarDays, Video, TrendingUp, Sparkles, Clock, PlayCircle, Megaphone, Pin, CalendarClock, Music, Scissors,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, StatCard, LevelBadge, ProgressBar, EmptyState, Avatar, RecitalCountdown } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { progressForSkills } from "@/lib/helpers";
import { ParentLoginStatus } from "@/components/ParentLoginStatus";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const audioUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND}${u}` : u || "");

function HomeExtras() {
  const [settings, setSettings] = useState(null);
  const [anns, setAnns] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/studio/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnns(r.data)).catch(() => {});
  }, []);
  const top = anns.slice(0, 3);
  const hasRecital = !!settings?.recital_date;
  return (
    <div className={`grid gap-6 mb-8 ${hasRecital ? "lg:grid-cols-3" : ""}`} data-testid="home-extras">
      {hasRecital && <div className="lg:col-span-1"><RecitalCountdown name={settings.recital_name} date={settings.recital_date} location={settings.recital_location} /></div>}
      <div className={hasRecital ? "lg:col-span-2" : ""}>
        <div className="bg-white rounded-2xl border border-gray-100 p-6 h-full">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head font-bold text-lg flex items-center gap-2"><Megaphone className="w-5 h-5 text-teal" />Announcements</h3>
            <button data-testid="view-all-announcements" onClick={() => navigate("/announcements")} className="text-sm text-teal-dark font-semibold hover:underline">View all</button>
          </div>
          {top.length === 0 ? <p className="text-sm text-muted-foreground">No announcements yet.</p> :
            <div className="space-y-2.5">
              {top.map((a) => (
                <button key={a.id} data-testid={`dash-announcement-${a.id}`} onClick={() => navigate("/announcements")} className="w-full text-left p-3 rounded-xl border border-gray-100 hover:bg-background transition-colors">
                  <div className="flex items-center gap-1.5">{a.pinned && <Pin className="w-3.5 h-3.5 text-teal" />}<p className="font-semibold text-sm">{a.title}</p></div>
                  {a.body && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{a.body}</p>}
                </button>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}

function DirectorDash() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard/director").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const c = data.counts;
  return (
    <div className="stagger-in">
      <PageHeader title="Director Dashboard" subtitle="Your whole studio at a glance." testid="director-dashboard" />
      <HomeExtras />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-dancers" icon={Sparkles} label="Active Dancers" value={c.dancers} accent="#2FB7B5" />
        <StatCard testid="stat-teachers" icon={GraduationCap} label="Teachers" value={c.teachers} accent="#000000" />
        <StatCard testid="stat-parents" icon={Heart} label="Parents" value={c.parents} accent="#FB923C" />
        <StatCard testid="stat-classes" icon={CalendarDays} label="Classes" value={c.classes} accent="#EAB308" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-head font-bold text-lg mb-4">Quick actions</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <QuickAction label="Manage People" icon={Users} onClick={() => navigate("/people")} testid="qa-people" />
            <QuickAction label="Classes & Enrollments" icon={CalendarDays} onClick={() => navigate("/classes")} testid="qa-classes" />
            <QuickAction label="Add Practice Video" icon={Video} onClick={() => navigate("/videos")} testid="qa-videos" />
            <QuickAction label="Review Advancement" icon={TrendingUp} onClick={() => navigate("/advancement")} testid="qa-advancement" badge={data.pending_advancement} />
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-head font-bold text-lg mb-1">Pending advancement</h3>
          <p className="text-sm text-muted-foreground mb-4">Requests awaiting your approval.</p>
          <div className="flex items-center gap-3">
            <span className="font-head text-5xl font-extrabold text-teal">{data.pending_advancement}</span>
            <span className="text-sm text-muted-foreground">requests<br />to review</span>
          </div>
        </div>
      </div>
      <ParentLoginStatus />

      <h3 className="font-head font-bold text-lg mt-8 mb-4">Recent practice videos</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.recent_videos.map((v) => (
          <div key={v.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
            <PlayCircle className="w-8 h-8 text-teal mb-2" />
            <p className="font-semibold">{v.title}</p>
            <p className="text-xs text-muted-foreground mt-1">{v.platform} · {v.section}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickAction({ label, icon: Icon, onClick, testid, badge }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="relative flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-background hover:bg-teal-light hover:border-teal transition-colors text-left">
      <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-gray-100">
        <Icon className="w-5 h-5 text-teal-dark" />
      </span>
      <span className="font-semibold text-sm">{label}</span>
      {badge > 0 && <span className="absolute top-3 right-3 bg-teal text-white text-xs font-bold rounded-full px-2 py-0.5">{badge}</span>}
    </button>
  );
}

function TeacherDash() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/dashboard/teacher").then((r) => setData(r.data));
    api.get("/coverage").then((r) => setCoverage(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const today = new Date().toISOString().slice(0, 10);
  const covering = coverage.filter((c) => c.covering_teacher_id === user?.profile_id && c.date >= today && c.status !== "cancelled");
  const myAbsences = coverage.filter((c) => c.original_teacher_id === user?.profile_id && c.date >= today && c.status !== "cancelled");
  const pretty = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="stagger-in">
      <PageHeader title="Teacher Dashboard" subtitle="Your assigned classes and dancers." testid="teacher-dashboard" />
      <HomeExtras />

      {(covering.length > 0 || myAbsences.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {covering.length > 0 && (
            <div data-testid="youre-covering" className="rounded-2xl p-5 text-white" style={{ background: "linear-gradient(135deg,#0F766E,#2FB7B5)" }}>
              <p className="text-xs font-bold uppercase tracking-widest text-white/80">You're Covering</p>
              {covering.slice(0, 3).map((c) => (
                <div key={c.id} className="mt-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-head font-bold">{pretty(c.date)}</p>
                    <p className="text-sm text-white/90">{c.class_name} · {c.class_time} · for {c.original_teacher_name}</p>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button data-testid={`cover-plan-${c.id}`} onClick={() => navigate("/lesson-plans")} className="text-[11px] font-bold bg-white/20 hover:bg-white/30 rounded-full px-2.5 py-1 whitespace-nowrap">Lesson plan</button>
                    <button data-testid={`cover-attend-${c.id}`} onClick={() => navigate("/attendance")} className="text-[11px] font-bold bg-white/20 hover:bg-white/30 rounded-full px-2.5 py-1 whitespace-nowrap">Attendance</button>
                    <button data-testid={`cover-pickup-${c.id}`} onClick={() => navigate("/pickup")} className="text-[11px] font-bold bg-white/20 hover:bg-white/30 rounded-full px-2.5 py-1 whitespace-nowrap">Pickup</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {myAbsences.length > 0 && (
            <div data-testid="my-absences" className="bg-white rounded-2xl border border-gray-100 p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Your Upcoming Coverage</p>
              {myAbsences.slice(0, 3).map((c) => (
                <div key={c.id} className="mt-2">
                  <p className="font-semibold text-sm">{pretty(c.date)} — {c.class_name}</p>
                  <p className="text-xs text-muted-foreground">Covered by {c.covering_teacher_name || "TBD"}</p>
                </div>
              ))}
              <button onClick={() => navigate("/coverage")} className="text-xs text-teal-dark font-semibold mt-2 hover:underline">Manage coverage</button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard testid="stat-my-classes" icon={CalendarDays} label="My Classes" value={data.classes.length} />
        <StatCard testid="stat-my-dancers" icon={Sparkles} label="My Dancers" value={data.dancer_count} accent="#FB923C" />
        <StatCard testid="stat-pending-rec" icon={TrendingUp} label="Pending Recs" value={data.pending_recommendations.length} accent="#EAB308" />
      </div>
      <h3 className="font-head font-bold text-lg mt-8 mb-4">Today's classes & rosters</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.classes.map((cl) => (
          <button key={cl.id} data-testid={`teacher-class-${cl.id}`} onClick={() => navigate("/assess")}
            className="text-left bg-white rounded-2xl border border-gray-100 p-5 hover:-translate-y-1 hover:shadow-md transition-all">
            <p className="font-head font-bold text-lg">{cl.name}</p>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{cl.day} · {cl.start_time}</p>
            <p className="text-sm mt-2 text-teal-dark font-semibold">Room {cl.room}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function DancerDash() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [skills, setSkills] = useState([]);
  const [prog, setProg] = useState({ skills: [] });
  const [rehearsals, setRehearsals] = useState([]);
  const [mymusic, setMymusic] = useState([]);
  useEffect(() => {
    api.get("/dashboard/dancer").then((r) => setData(r.data));
    api.get("/curriculum/levels").then((r) => setLevels(r.data));
    api.get("/curriculum/skills").then((r) => setSkills(r.data));
    api.get("/production/my-rehearsals").then((r) => setRehearsals(r.data)).catch(() => {});
    api.get("/production/my-music").then((r) => setMymusic(r.data)).catch(() => {});
  }, []);
  const respond = async (rid, status) => {
    try { await api.post(`/production/rehearsals/${rid}/respond`, { status }); toast.success(status === "cannot" ? "Marked can't attend" : "Marked attending"); api.get("/production/my-rehearsals").then((r) => setRehearsals(r.data)); }
    catch (e) { toast.error("Error"); }
  };
  useEffect(() => {
    if (data?.dancer) api.get(`/curriculum/progress/${data.dancer.id}`).then((r) => setProg(r.data));
  }, [data]);
  if (!data) return null;
  const levelMap = Object.fromEntries(levels.map((l) => [l.id, l]));
  return (
    <div className="stagger-in">
      <PageHeader title={`Hi, ${data.dancer?.preferred_name || data.dancer?.first_name || "Dancer"}! 👋`}
        subtitle="Ready to dance? Here's your journey." testid="dancer-dashboard" />
      <HomeExtras />

      <div className="grid md:grid-cols-2 gap-4">
        {data.styles.map((s) => {
          const lvl = levelMap[s.current_level_id];
          const reqIds = skills.filter((sk) => sk.level_id === s.current_level_id && sk.required).map((sk) => sk.id);
          const pct = progressForSkills(prog.skills, reqIds);
          return (
            <div key={s.id} data-testid={`dancer-style-${s.style_id}`} className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="font-head font-bold text-lg">{lvl?.style_id ? "" : ""}Current Level</p>
                <LevelBadge name={lvl?.name} color={lvl?.color} />
              </div>
              <ProgressBar value={pct} color={lvl?.color || "#2FB7B5"} />
              <p className="text-sm text-muted-foreground mt-2">{pct}% toward next level</p>
            </div>
          );
        })}
      </div>

      <h3 className="font-head font-bold text-lg mt-8 mb-4 flex items-center gap-2"><Video className="w-5 h-5 text-teal" />New practice videos</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.videos.length === 0 && <EmptyState icon={Video} title="No practice videos yet!" hint="Get ready to dance." testid="dancer-no-videos" />}
        {data.videos.map((v) => (
          <div key={v.id} className="bg-white rounded-2xl border border-gray-100 p-5">
            <PlayCircle className="w-8 h-8 text-teal mb-2" />
            <p className="font-semibold">{v.title}</p>
            <p className="text-xs text-muted-foreground mt-1">{v.routine_name}</p>
          </div>
        ))}
      </div>

      {rehearsals.filter((r) => r.date >= new Date().toISOString().slice(0, 10)).length > 0 && (
        <div data-testid="dancer-rehearsals">
          <h3 className="font-head font-bold text-lg mt-8 mb-4 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-teal" />My Rehearsals</h3>
          <div className="space-y-3">
            {rehearsals.filter((r) => r.date >= new Date().toISOString().slice(0, 10)).map((r) => {
              const to12 = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const hh = ((h + 11) % 12) + 1; return `${hh}:${String(m).padStart(2, "0")} ${ap}`; };
              const pretty = new Date(r.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
              return (
                <div key={r.id} data-testid={`dancer-rehearsal-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-head font-bold">{pretty}</p>
                      <p className="text-sm text-muted-foreground">{to12(r.start_time)}{r.end_time ? ` – ${to12(r.end_time)}` : ""}{r.room ? ` · ${r.room}` : ""}{r.production_name ? ` · ${r.production_name}` : ""}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.my_routine_titles.map((t, i) => <span key={i} className="inline-block rounded-full text-[10px] px-2 py-0.5 font-semibold bg-teal-light text-teal-dark">{t}</span>)}
                    </div>
                  </div>
                  {r.notes && <p className="text-sm mt-2 text-muted-foreground">{r.notes}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <Button size="sm" className={`rounded-full h-7 ${r.my_response === "attending" ? "bg-teal" : "bg-teal/70"} hover:bg-teal-dark`} data-testid={`reh-attending-${r.id}`} onClick={() => respond(r.id, "attending")}>Attending</Button>
                    <Button size="sm" variant="outline" className={`rounded-full h-7 ${r.my_response === "cannot" ? "border-red-400 text-red-600" : ""}`} data-testid={`reh-cannot-${r.id}`} onClick={() => respond(r.id, "cannot")}>Can't attend</Button>
                    {r.my_response && <span className="text-xs text-muted-foreground">You: {r.my_response === "cannot" ? "Can't attend" : "Attending"}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mymusic.length > 0 && (
        <div data-testid="dancer-music">
          <h3 className="font-head font-bold text-lg mt-8 mb-4 flex items-center gap-2"><Music className="w-5 h-5 text-teal" />My Performance Music</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {mymusic.map((m) => {
              const perf = audioUrl(m.performance_url);
              return (
                <div key={m.id} data-testid={`dancer-music-${m.id}`} className="bg-white rounded-2xl border border-gray-100 p-4">
                  <p className="font-head font-bold flex items-center gap-2 flex-wrap">{m.dance_name}
                    {m.has_cut ? <span className="inline-flex items-center gap-1 rounded-full text-[10px] px-2 py-0.5 font-semibold bg-teal text-white"><Scissors className="w-3 h-3" />Cut · performance track</span>
                      : <span className="inline-block rounded-full text-[10px] px-2 py-0.5 font-semibold bg-gray-100 text-gray-600">Approved song</span>}
                  </p>
                  <p className="text-sm text-muted-foreground">{m.song_title}{m.artist ? ` — ${m.artist}` : ""}</p>
                  {perf ? <audio controls preload="none" src={perf} className="w-full h-9 mt-2" data-testid={`dancer-audio-${m.id}`} /> : <p className="text-xs text-muted-foreground mt-2">Audio coming soon.</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ParentDash() {
  const [dancers, setDancers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [logins, setLogins] = useState([]);
  const [shownPin, setShownPin] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/people/dancers").then((r) => setDancers(r.data));
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/people/my-dancer-logins").then((r) => setLogins(r.data)).catch(() => {});
  }, []);
  const resetDancerPin = async (did) => { try { const r = await api.post(`/people/my-dancers/${did}/reset-pin`); setShownPin({ did, ...r.data }); toast.success("New PIN generated — shown once"); } catch { toast.error("Could not reset PIN"); } };
  return (
    <div className="stagger-in">
      <PageHeader title="Parent Home" subtitle="Follow every dancer in your family." testid="parent-dashboard" />
      <HomeExtras />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {dancers.map((d) => (
          <button key={d.id} data-testid={`parent-dancer-${d.id}`} onClick={() => navigate("/progress")}
            className="text-left bg-white rounded-2xl border border-gray-100 p-6 hover:-translate-y-1 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <Avatar first={d.first_name} last={d.last_name} photo={d.photo} size={52} />
              <div>
                <p className="font-head font-bold text-lg">{d.first_name} {d.last_name}</p>
                <p className="text-sm text-muted-foreground">Grade {d.grade}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-teal-dark font-semibold flex items-center gap-1">View progress <TrendingUp className="w-4 h-4" /></p>
          </button>
        ))}
      </div>

      <h3 className="font-head font-bold text-lg mt-8 mb-4">Dancer Login Information</h3>
      <div className="bg-white rounded-2xl border border-gray-100 divide-y" data-testid="dancer-login-info">
        {logins.length === 0 ? <p className="text-sm text-muted-foreground p-5">No dancers linked yet.</p> : logins.map((l) => (
          <div key={l.dancer_id} data-testid={`dancer-login-${l.dancer_id}`} className="flex items-center justify-between gap-3 p-4 flex-wrap">
            <div>
              <p className="font-semibold">{l.name}</p>
              <p className="text-sm text-muted-foreground">Username: <b>{l.username || "—"}</b>{l.consent_required && !l.consent_granted ? <span className="ml-2 text-xs text-amber-700">· sign-in blocked until consent</span> : ""}</p>
              {shownPin?.did === l.dancer_id && <p className="text-sm text-teal-dark mt-1">New PIN: <b>{shownPin.pin}</b> (expires in {shownPin.expires_days} days — shown once)</p>}
            </div>
            <Button size="sm" variant="outline" className="rounded-full" data-testid={`reset-pin-${l.dancer_id}`} onClick={() => resetDancerPin(l.dancer_id)}>{l.has_pin ? "Reset PIN" : "Set PIN"}</Button>
          </div>
        ))}
      </div>

      <h3 className="font-head font-bold text-lg mt-8 mb-4 flex items-center gap-2"><Video className="w-5 h-5 text-teal" />Recent practice videos</h3>
      {videos.length === 0 ? <EmptyState icon={Video} title="No practice videos yet" hint="Your dancers' class videos will show here." /> :
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.slice(0, 6).map((v) => (
            <button key={v.id} data-testid={`parent-video-${v.id}`} onClick={() => navigate("/videos")} className="text-left bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <PlayCircle className="w-8 h-8 text-teal mb-2" />
              <p className="font-semibold">{v.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{v.platform} · {v.routine_name || v.section}</p>
            </button>
          ))}
        </div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "director") return <DirectorDash />;
  if (user?.role === "teacher") return <TeacherDash />;
  if (user?.role === "parent") return <ParentDash />;
  return <DancerDash />;
}
